import { ref, toRefs, computed, watch } from 'vue'
import { useHttp } from '@core/composables/useHttp'
import { watchDebounced } from '@vueuse/core'
import { useOrder } from '@core/composables/order/useOrder'
import { useToast } from 'vue-toastification'

export const useTargetingList = (props) => {
  //////////////////////////////////////////////////////////////////////////
  //// Props

  const { resourceUrl, payloadKeyName, restrictSelection, singleItem } =
    toRefs(props)

  //////////////////////////////////////////////////////////////////////////
  //// State

  const DEBOUNCE_DELAY = 500
  const MAX_AVAILABLE_SELECTED_ITEMS = 4

  const items = ref([])
  const selectedItems = ref([...(props.selectedItems ?? [])])

  watch(
    () => props.selectedItems,
    (newSelectedItems) => {
      selectedItems.value = [...(newSelectedItems ?? [])]
    },
    { deep: true }
  )

  const search = ref(null)
  const currentPage = ref(0)
  const perPage = ref(20)
  const lastPage = ref(null)

  const syncItems = () => {
    if (!selectedItems.value.length) return

    selectedItems.value.forEach((selectedItem) => {
      const itemPosition = items.value.findIndex(
        (item) => item.id === selectedItem.id
      )

      if (itemPosition > -1) {
        items.value.splice(itemPosition, 1)
      }
    })
  }

  //////////////////////////////////////////////////////////////////////////
  //// Composables

  const { isLoading: isItemsFetching, execute } = useHttp()

  const toast = useToast()
  const { useUpdateOrder } = useOrder()
  const { isLoading: isOrderUpdating, updateOrder } = useUpdateOrder()

  //////////////////////////////////////////////////////////////////////////
  //// Computed

  const payload = computed(() => ({
    page: currentPage.value + 1,
    per_page: perPage.value,
    search: search.value,
  }))

  const isReachedLastPage = computed(
    () => lastPage.value != null && currentPage.value >= lastPage.value
  )

  const getPaginationMeta = (apiData) => apiData?.meta ?? apiData ?? {}

  const readPaginationValue = (pagination, camelKey, snakeKey) =>
    pagination?.[camelKey] ?? pagination?.[snakeKey]

  const selectedItemsIds = computed(() => {
    const items = selectedItems.value.map((selectedItem) => selectedItem.id)

    return singleItem?.value ? items.toString() : items
  })

  const buildItemsTree = (apiItems) => {
    if (!apiItems?.length) return []

    const selectedItemsIds = selectedItems.value.map((item) => item.id)

    return apiItems
      .filter((item) => !selectedItemsIds.includes(item.id))
      .flatMap((item) => {
        const subInfluencers = item.subInfluencers ?? item.sub_influencers
        const subIndustryTargets =
          item.subIndustryTargets ?? item.sub_industry_targets
        const subLocalAreas = item.subLocalAreas ?? item.sub_local_areas

        if (subInfluencers?.length) {
          return subInfluencers.map((subItem) => ({
            ...subItem,
            name: `${item.name} - ${subItem.name}`,
          }))
        }

        if (subIndustryTargets?.length) {
          return [
            item,
            ...subIndustryTargets.map((subItem) => ({
              ...subItem,
              name: `${item.name} - ${subItem.name}`,
            })),
          ]
        }

        if (subLocalAreas?.length) {
          return [
            item,
            ...subLocalAreas.map((subItem) => ({
              ...subItem,
              name: `${item.name} - ${subItem.name}`,
            })),
          ]
        }

        return [item]
      })
  }

  //////////////////////////////////////////////////////////////////////////
  //// Handlers

  const handleSuccessfulFetching = (apiData) => {
    items.value.push(...buildItemsTree(apiData?.data))

    syncItems()

    const pagination = getPaginationMeta(apiData)
    currentPage.value = readPaginationValue(
      pagination,
      'currentPage',
      'current_page'
    )
    perPage.value = readPaginationValue(pagination, 'perPage', 'per_page')
    lastPage.value = readPaginationValue(pagination, 'lastPage', 'last_page')
  }

  const onIntersectionObserver = (intersectionObserverEntry) => {
    const { isIntersecting } = intersectionObserverEntry[0]

    if (!isIntersecting) return
    if (payload.value.search) return
    if (isReachedLastPage.value) return

    execute(
      { url: resourceUrl.value, params: payload.value },
      handleSuccessfulFetching
    )
  }

  const addSelectedItem = (selectedItem) => {
    if (singleItem.value && selectedItems.value.length > 0) return

    if (
      restrictSelection.value &&
      selectedItems.value.length === MAX_AVAILABLE_SELECTED_ITEMS
    ) {
      toast.error(
        `Sorry, there is a maximum of ${MAX_AVAILABLE_SELECTED_ITEMS} industry targets allowed.`
      )
      return
    }

    const alreadySelected = selectedItems.value.find(
      (item) => item.id === selectedItem.id
    )
    const removeIndexPosition = items.value.findIndex(
      (item) => item.id === selectedItem.id
    )
    if (alreadySelected) return

    items.value.splice(removeIndexPosition, 1)

    selectedItems.value = [
      ...selectedItems.value,
      { ...selectedItem, removeIndexPosition },
    ]

    updateOrder({ [payloadKeyName.value]: selectedItemsIds.value })
  }

  const removeSelectedItem = (selectedItem) => {
    items.value.splice(selectedItem.removeIndexPosition, 0, selectedItem)

    selectedItems.value = selectedItems.value.filter(
      (item) => item.id !== selectedItem.id
    )

    updateOrder({ [payloadKeyName.value]: selectedItemsIds.value })
  }

  const addAllItems = () => {
    execute(
      { url: resourceUrl.value, params: { perPage: 9999 } },
      ({ data }) => {
        const buildCountryIds = () => data.map((country) => country.id)

        const payload = {
          countries: buildCountryIds(),
        }

        updateOrder(payload, () => {
          selectedItems.value = data.map((item, index) => ({
            ...item,
            removeIndexPosition: index,
          }))
          items.value = []
        })
      }
    )
  }
  const removeAllSelectedItems = () => {
    if (!selectedItems.value.length) return

    selectedItems.value.forEach((selectedItem) => {
      items.value.splice(selectedItem.removeIndexPosition, 0, selectedItem)
    })

    selectedItems.value = []

    updateOrder({ [payloadKeyName.value]: selectedItemsIds.value })
  }

  execute(
    { url: resourceUrl.value, params: payload.value },
    handleSuccessfulFetching
  )

  watchDebounced(
    search,
    (searchValue) =>
      execute(
        { url: resourceUrl.value, params: { search: searchValue } },
        (searchResult) => {
          items.value = [...buildItemsTree(searchResult?.data)]
        }
      ),
    {
      debounce: DEBOUNCE_DELAY,
    }
  )

  return {
    search,
    items,
    isItemsFetching,
    selectedItems,
    selectedItemsIds,
    addSelectedItem,
    removeSelectedItem,
    removeAllSelectedItems,
    addAllItems,
    onIntersectionObserver,
    isOrderUpdating,
  }
}
